import { Form, Formik, FormikProps, getIn } from "formik";
import i18next from "i18next";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import {
  IRangeOption,
  ISurveyDisplayRules,
  ISurveySection,
} from "../../../../../@types/Surveys";
import {
  IOption,
  SelectDropdown,
} from "../../../../../components/SelectDropdown";
import { TextField } from "../../../../../components/TextField";
import { GraphTypes } from "../../../../../constants/surveys";
import { getRandomHexColor } from "../../../../../utils/colorGenerator";
import { IDisplayRulesOptions } from "../../SurveyBuilder";
import { AnswerPlotForm } from "./Components/AnswerPlotForm/AnswerPlotForm";
import { DomainAndTotalScoreForm } from "./Components/DomainAndTotalScoreForm";
import { TotalScoreWithLegendForm } from "./Components/TotalScoreWithLegendForm";
import {
  DisplayRulescontainer,
  DisplaySubTitle,
  DisplayTitle,
  MultipleInputsRow,
  RowElement,
  selectCss,
  mediumTextFieldClassName,
  infoTextFieldClassName,
  ErrorMessage,
  WarningMessage,
} from "./DisplayTab.styles";

type RangeOptionFormik = Partial<IRangeOption>;

interface ISurveyDisplayBaseProps {
  displayRulesOptions: IDisplayRulesOptions;
  hasScoreStrategy: boolean;
  sections: ISurveySection[];
  domains: string[];
}

interface ISurveyDisplayFormikProps extends ISurveyDisplayBaseProps {
  displayRules: ISurveyDisplayRules;
}

interface ISurveyDisplayProps extends ISurveyDisplayBaseProps {
  displaySettingsFormik: FormikProps<ISurveyDisplayRules>;
}

function SurveyBuilderDisplayTab({
  displayRulesOptions,
  sections,
  displaySettingsFormik,
  hasScoreStrategy,
  domains,
}: ISurveyDisplayProps) {
  const { t } = useTranslation();
  const [domainOptions, setDomainOptions] = useState<IOption[]>([]);

  const defaultRange = {
    start: undefined,
    end: undefined,
    color: getRandomHexColor(),
    name: undefined,
    startComparator: undefined,
    endComparator: undefined,
  };

  const defaultLabel = {
    labelIds: undefined,
    text: undefined,
    order: displaySettingsFormik.values.yAxisLabels?.length ?? 0,
  };

  const defaultLegend = {
    legendIds: undefined,
    text: undefined,
    color: getRandomHexColor(),
    order: displaySettingsFormik.values.legends?.length ?? 0,
  };

  const defaultDomainAnTotalLegend = {
    domain: undefined,
    text: undefined,
    color: getRandomHexColor(),
    order: displaySettingsFormik.values.domainAndTotalLegends?.length ?? 0,
  };

  const shouldShowErrorMessage = (field: "yAxisMin" | "yAxisMax") => {
    return displaySettingsFormik.touched[field] ||
      displaySettingsFormik.touched.ranges
      ? getIn(displaySettingsFormik.errors, field)
      : "";
  };

  const addRange = () => {
    displaySettingsFormik.setFieldValue("ranges", [
      ...(displaySettingsFormik.values.ranges ?? []),
      defaultRange,
    ]);
  };

  const addLabel = () => {
    displaySettingsFormik.setFieldValue("yAxisLabels", [
      ...(displaySettingsFormik.values.yAxisLabels ?? []),
      defaultLabel,
    ]);
  };

  const addLegend = () => {
    displaySettingsFormik.setFieldValue("legends", [
      ...(displaySettingsFormik.values.legends ?? []),
      defaultLegend,
    ]);
  };

  const addDomainAndTotalLegend = () => {
    displaySettingsFormik.setFieldValue("domainAndTotalLegends", [
      ...(displaySettingsFormik.values.domainAndTotalLegends ?? []),
      defaultDomainAnTotalLegend,
    ]);
  };

  const resetTotalScoreGraphValues = () => {
    const updatedDisplayRules = displaySettingsFormik.values;
    updatedDisplayRules.ranges = undefined;
    displaySettingsFormik.setValues(updatedDisplayRules);
  };

  const resetAnswerPlotValues = () => {
    const updatedDisplayRules = displaySettingsFormik.values;
    updatedDisplayRules.dataSourceType = undefined;
    updatedDisplayRules.legends = undefined;
    updatedDisplayRules.yAxisLabels = undefined;
    displaySettingsFormik.setValues(updatedDisplayRules);
  };

  const resetDomainAndTotalGraphValues = () => {
    const updatedDisplayRules = displaySettingsFormik.values;
    updatedDisplayRules.domainAndTotalLegends = undefined;
    displaySettingsFormik.setValues(updatedDisplayRules);
  };

  const resetYAxisMinAndMax = () => {
    displaySettingsFormik.setFieldValue("yAxisMin", undefined);
    displaySettingsFormik.setFieldValue("yAxisMax", undefined);
  };

  const handleGraphTypeChange = () => {
    const { type } = displaySettingsFormik.values;

    if (type === GraphTypes.totalScoreGraphWithLegend) {
      resetDomainAndTotalGraphValues();
      resetAnswerPlotValues();
      if (!displaySettingsFormik.values.ranges?.length) {
        addRange();
      }
    } else if (type === GraphTypes.answerPlot) {
      resetDomainAndTotalGraphValues();
      resetTotalScoreGraphValues();

      resetYAxisMinAndMax();

      if (!displaySettingsFormik.values.yAxisLabels?.length) {
        addLabel();
      }
      if (!displaySettingsFormik.values.legends?.length) {
        addLegend();
      }
    } else if (type === GraphTypes.domainAndTotalGraphWithLegend) {
      resetTotalScoreGraphValues();
      resetAnswerPlotValues();
      if (!displaySettingsFormik.values.domainAndTotalLegends?.length) {
        addDomainAndTotalLegend();
      }
    } else if (type === GraphTypes.tableView) {
      resetAnswerPlotValues();
      resetTotalScoreGraphValues();
      resetDomainAndTotalGraphValues();

      resetYAxisMinAndMax();
    }
  };

  const renderGraphTypeForm = () => {
    switch (displaySettingsFormik.values.type) {
      case GraphTypes.totalScoreGraphWithLegend:
        return (
          <TotalScoreWithLegendForm
            displaySettingsFormik={displaySettingsFormik}
            displayRulesOptions={displayRulesOptions}
            addRangeOption={addRange}
          />
        );
      case GraphTypes.answerPlot:
        return (
          <AnswerPlotForm
            displaySettingsFormik={displaySettingsFormik}
            sections={sections}
            addLabel={addLabel}
            addLegend={addLegend}
          />
        );
      case GraphTypes.domainAndTotalGraphWithLegend:
        return (
          <DomainAndTotalScoreForm
            displaySettingsFormik={displaySettingsFormik}
            addDomainAndTotalLegend={addDomainAndTotalLegend}
            domainOptions={domainOptions}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    handleGraphTypeChange();
  }, [displaySettingsFormik.values.type]);

  useEffect(() => {
    const domainsToUse: IOption[] = domains.map((d) => {
      return { label: d, value: d, key: uuidv4() } as IOption;
    });

    domainsToUse.push({
      label: t("surveys_builder_display_total"),
      value: "Total",
      key: uuidv4(),
    });

    setDomainOptions(domainsToUse);
  }, [domains]);

  return (
    <>
      <DisplayTitle>{t("surveys_builder_display_title")}</DisplayTitle>
      <DisplayRulescontainer>
        <MultipleInputsRow>
          <RowElement>
            <DisplaySubTitle>
              {t("surveys_builder_display_graphType")} *
            </DisplaySubTitle>
            <SelectDropdown
              className={selectCss()}
              width={248}
              height={42}
              showSearch
              optionFilterProp="label"
              value={displaySettingsFormik.values.type}
              onValueChange={(value) => {
                displaySettingsFormik.setFieldValue("type", value.toString());
              }}
              options={displayRulesOptions.displayRulesTypes}
            />
            {!hasScoreStrategy &&
              displaySettingsFormik.values.type !== GraphTypes.tableView && (
                <WarningMessage>
                  {t("surveys_builder_display_scoreStrategyHint")}
                </WarningMessage>
              )}
          </RowElement>
          {(displaySettingsFormik.values.type ===
            GraphTypes.totalScoreGraphWithLegend ||
            displaySettingsFormik.values.type ===
              GraphTypes.domainAndTotalGraphWithLegend) && (
            <>
              <RowElement>
                <DisplaySubTitle>
                  {t("surveys_builder_display_yAxisMin")}
                </DisplaySubTitle>
                <TextField
                  label=""
                  placeholder={t("surveys_builder_display_minPlaceholder")}
                  name="yAxisMin"
                  value={displaySettingsFormik.values.yAxisMin}
                  onChange={displaySettingsFormik.handleChange}
                  className={mediumTextFieldClassName()}
                  backgroudColor="white"
                  onBlur={displaySettingsFormik.handleBlur}
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("yAxisMin")}
                </ErrorMessage>
              </RowElement>
              <RowElement>
                <DisplaySubTitle>
                  {t("surveys_builder_display_yAxisMax")}
                </DisplaySubTitle>
                <TextField
                  label=""
                  placeholder={t("surveys_builder_display_maxPlaceholder")}
                  name="yAxisMax"
                  value={displaySettingsFormik.values.yAxisMax}
                  onChange={displaySettingsFormik.handleChange}
                  className={mediumTextFieldClassName()}
                  backgroudColor="white"
                  onBlur={displaySettingsFormik.handleBlur}
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("yAxisMax")}
                </ErrorMessage>
              </RowElement>
            </>
          )}
        </MultipleInputsRow>
        <RowElement>
          <DisplaySubTitle>
            {t("surveys_builder_display_hcpInformation")}
          </DisplaySubTitle>
          <TextField
            label=""
            placeholder={t("surveys_builder_display_hcpInformationPlaceholder")}
            name="hcpInformation"
            value={displaySettingsFormik.values.hcpInformation}
            onChange={displaySettingsFormik.handleChange}
            className={infoTextFieldClassName()}
            backgroudColor="white"
          />
        </RowElement>
        <RowElement>
          <DisplaySubTitle>
            {t("surveys_builder_display_patientInformation")}
          </DisplaySubTitle>
          <TextField
            label=""
            placeholder={t(
              "surveys_builder_display_patientInformationPlaceholder"
            )}
            name="patientInformation"
            value={displaySettingsFormik.values.patientInformation}
            onChange={displaySettingsFormik.handleChange}
            className={infoTextFieldClassName()}
            backgroudColor="white"
          />
        </RowElement>

        {renderGraphTypeForm()}
      </DisplayRulescontainer>
    </>
  );
}

export const DisplaySettingsFormik = forwardRef<
  FormikProps<ISurveyDisplayRules>,
  ISurveyDisplayFormikProps
>(
  (
    { displayRules, displayRulesOptions, hasScoreStrategy, sections, domains },
    ref
  ) => {
    const { t } = useTranslation();

    const handleOnSubmitForm = () => {};

    const checkOverlap = (
      range1: RangeOptionFormik,
      range2: RangeOptionFormik
    ) => {
      if (
        range1.start === undefined ||
        range1.startComparator === undefined ||
        range1.end === undefined ||
        range1.endComparator === undefined ||
        range2.start === undefined ||
        range2.startComparator === undefined
      ) {
        return false;
      }

      if (
        range1.start === range2.start &&
        !(
          (range1.startComparator === ">" && range2.startComparator === "=") ||
          (range1.startComparator === "=" && range2.startComparator === ">")
        )
      ) {
        return true;
      }
      if (range1.startComparator === "=" && range2.startComparator !== "=") {
        return false;
      }

      if (range1.startComparator === "=" && range2.startComparator === "=") {
        return false;
      }

      if (
        range2.start < range1.end ||
        (range2.start === range1.end &&
          range1.endComparator.includes("=") &&
          range2.startComparator !== ">")
      ) {
        return true;
      }
      return false;
    };

    const rangesValidationSchema = yup.object().shape({
      start: yup.number().when("$type", {
        is: GraphTypes.totalScoreGraphWithLegend,
        then: yup
          .number()
          .typeError(t("surveys_builder_display_validation_valueNumber"))
          .lessThan(
            yup.ref("$yAxisMax"),
            t("surveys_builder_display_validation_valueSmallerThanMax")
          )
          .min(
            yup.ref("$yAxisMin"),
            t("surveys_builder_display_validation_valueNotSmallerThanMin")
          )
          .required(t("surveys_builder_display_validation_startValueRequired")),
        otherwise: yup.number(),
      }),

      end: yup.number().when(["$type", "startComparator"], {
        is: (type: string, startComparator: string) =>
          type === GraphTypes.totalScoreGraphWithLegend &&
          startComparator !== "=",
        then: yup
          .number()
          .typeError(t("surveys_builder_display_validation_valueNumber"))
          .moreThan(
            yup.ref("$yAxisMin"),
            t("surveys_builder_display_validation_valueBiggerThanMin")
          )
          .max(
            yup.ref("$yAxisMax"),
            t("surveys_builder_display_validation_valueNotBiggerThanMax")
          )
          .moreThan(
            yup.ref("start"),
            t("surveys_builder_display_validation_valueGreaterThanStart")
          )
          .required(t("surveys_builder_display_validation_endValueRequired")),
        otherwise: yup.number(),
      }),

      startComparator: yup.string().when("$type", {
        is: GraphTypes.totalScoreGraphWithLegend,
        then: yup
          .string()
          .required(
            t("surveys_builder_display_validation_startComparatorRequired")
          ),
        otherwise: yup.string(),
      }),

      endComparator: yup.string().when(["$type", "startComparator"], {
        is: (type: string, startComparator: string) =>
          type === GraphTypes.totalScoreGraphWithLegend &&
          startComparator !== "=",
        then: yup
          .string()
          .required(
            t("surveys_builder_display_validation_endComparatorRequired")
          ),
        otherwise: yup.string(),
      }),

      color: yup.string().when("$type", {
        is: GraphTypes.totalScoreGraphWithLegend,
        then: yup
          .string()
          .required(t("surveys_builder_display_validation_colorRequired")),
        otherwise: yup.string(),
      }),

      name: yup.string().when("$type", {
        is: GraphTypes.totalScoreGraphWithLegend,
        then: yup
          .string()
          .required(t("surveys_builder_display_validation_nameRequired")),
        otherwise: yup.string(),
      }),
    });

    const formValidationSchema = yup.object().shape({
      type: yup.string(),
      hcpInformation: yup.string(),
      patientInformation: yup.string(),
      yAxisMin: yup.number().when("type", {
        is: (val: string) =>
          val === GraphTypes.totalScoreGraphWithLegend ||
          val === GraphTypes.domainAndTotalGraphWithLegend,
        then: yup
          .number()
          .typeError(t("surveys_builder_display_validation_mustBeNumber"))
          .min(0, t("surveys_builder_display_validation_yAxisMinNegative"))
          .required(t("surveys_builder_display_validation_yAxisMinRequired")),
        otherwise: yup.number().nullable(),
      }),
      yAxisMax: yup.number().when("type", {
        is: (val: string) =>
          val === GraphTypes.totalScoreGraphWithLegend ||
          val === GraphTypes.domainAndTotalGraphWithLegend,
        then: yup
          .number()
          .typeError(t("surveys_builder_display_validation_mustBeNumber"))
          .moreThan(0, t("surveys_builder_display_validation_greaterThanZero"))
          .moreThan(
            yup.ref("yAxisMin"),
            t("surveys_builder_display_validation_biggerThanYAxisMin")
          )
          .required(t("surveys_builder_display_validation_yAxisMaxRequired")),
        otherwise: yup.number().nullable(),
      }),
      ranges: yup.array().when("type", {
        is: GraphTypes.totalScoreGraphWithLegend,
        then: yup
          .array()
          .of(rangesValidationSchema)
          // EN-5191: deliberately NOT translated. SurveyBuilder filters this exact string
          // out of the error summary (its `internalMessages` list), so it never reaches a
          // user — translating it here alone would start showing it.
          .required("An array is required")
          .min(1, t("surveys_builder_display_validation_rangeOptionRequired"))
          .test(
            "no-overlapping-ranges",
            t("surveys_builder_display_validation_intervalsOverlap"),
            function overlapTest(ranges) {
              if (!ranges || ranges.length < 2) return true;

              const validRanges = ranges.filter(
                (range) =>
                  typeof range.start === "number" &&
                  typeof range.end === "number"
              );

              if (validRanges.length < 2) return true;

              const sortedRanges = [...validRanges].sort(
                (a, b) => (a.start ?? 0) - (b.start ?? 0)
              );

              for (let i = 0; i < sortedRanges.length - 1; i += 1) {
                const current = sortedRanges[i];
                const next = sortedRanges[i + 1];

                if (checkOverlap(current, next)) {
                  return false;
                }
              }
              return true;
            }
          ),
        otherwise: yup.array(),
      }),
      dataSourceType: yup.string().when("type", {
        is: GraphTypes.answerPlot,
        then: yup
          .string()
          .required(
            t("surveys_builder_display_validation_dataSourceTypeRequired")
          ),
        otherwise: yup.string(),
      }),
      yAxisLabels: yup.array().when("type", {
        is: GraphTypes.answerPlot,
        then: yup
          .array()
          .of(
            yup.object().shape({
              labelIds: yup
                .array()
                .min(
                  1,
                  t("surveys_builder_display_validation_selectAtLeastOne")
                )
                .required(
                  t("surveys_builder_display_validation_labelSelectionRequired")
                ),
              text: yup
                .string()
                .required(
                  t("surveys_builder_display_validation_labelNameRequired")
                ),
            })
          )
          // EN-5191: deliberately NOT translated. SurveyBuilder filters this exact string
          // out of the error summary (its `internalMessages` list), so it never reaches a
          // user — translating it here alone would start showing it.
          .required("An array is required")
          .min(1, t("surveys_builder_display_validation_labelOptionRequired")),
        otherwise: yup.array(),
      }),
      legends: yup.array().when("type", {
        is: GraphTypes.answerPlot,
        then: yup
          .array()
          .of(
            yup.object().shape({
              legendIds: yup
                .array()
                .min(
                  1,
                  t("surveys_builder_display_validation_selectAtLeastOne")
                )
                .required(
                  t(
                    "surveys_builder_display_validation_legendSelectionRequired"
                  )
                ),
              text: yup
                .string()
                .required(
                  t("surveys_builder_display_validation_legendNameRequired")
                ),
              color: yup
                .string()
                .required(
                  t("surveys_builder_display_validation_legendColorRequired")
                ),
            })
          )
          // EN-5191: deliberately NOT translated. SurveyBuilder filters this exact string
          // out of the error summary (its `internalMessages` list), so it never reaches a
          // user — translating it here alone would start showing it.
          .required("An array is required")
          .min(1, t("surveys_builder_display_validation_labelOptionRequired")),
        otherwise: yup.array(),
      }),
      domainAndTotalLegends: yup.array().when("type", {
        is: GraphTypes.domainAndTotalGraphWithLegend,
        then: yup
          .array()
          .of(
            yup.object().shape({
              domain: yup
                .string()
                .required(() =>
                  i18next.t("surveys_builder_display_domainRequired")
                ),
              name: yup
                .string()
                .required(
                  t("surveys_builder_display_validation_legendNameRequired")
                ),
              color: yup
                .string()
                .required(
                  t("surveys_builder_display_validation_legendColorRequired")
                ),
            })
          )
          // EN-5191: deliberately NOT translated. SurveyBuilder filters this exact string
          // out of the error summary (its `internalMessages` list), so it never reaches a
          // user — translating it here alone would start showing it.
          .required("An array is required")
          .min(1, () => i18next.t("surveys_builder_display_legendRequired")),
        otherwise: yup.array(),
      }),
    });

    return (
      <Formik<ISurveyDisplayRules>
        innerRef={ref}
        initialValues={displayRules}
        onSubmit={handleOnSubmitForm}
        validationSchema={formValidationSchema}
        enableReinitialize
      >
        {(formik) => (
          <Form>
            <SurveyBuilderDisplayTab
              sections={sections}
              hasScoreStrategy={hasScoreStrategy}
              displaySettingsFormik={formik}
              displayRulesOptions={displayRulesOptions}
              domains={domains}
            />
          </Form>
        )}
      </Formik>
    );
  }
);
